import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";

import { AGE_GROUPS, CATEGORIES } from "@/lib/constants";
import { Navbar } from "@/components/Navbar";
import { PHONE_PLACEHOLDER, ADDRESS_PLACEHOLDER, SAUDI_CITIES, COUNTRY } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { TrustBadges } from "@/components/TrustBadges";
import { WishlistSection } from "@/components/WishlistSection";
import { compressImage, formatBytes } from "@/lib/image-compress";
import { pageHead } from "@/lib/seo";

export const Route = createFileRoute("/profile")({
  head: () => pageHead({ path: "/profile", title: "Your profile — Book Swap", description: "Manage your Book Swap profile, wishlist, badges and trust rating.", noindex: true }),
  component: Profile,
});

function Profile() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", phone_number: "", age_group: "", address: "", bio: "", favorite_quote: "", interested_categories: [] as string[], avatar_url: "" });
  const [trust, setTrust] = useState({ verified: false, completed_swaps: 0, response_rate: 100, strikes: 0 });
  const [stats, setStats] = useState({ listed: 0, since: "", trust: 0, exchanges: 0 });
  const [standing, setStanding] = useState<{ strikes: number; last_strike_at: string | null; strikes_reset_at: string | null }>({
    strikes: 0, last_strike_at: null, strikes_reset_at: null,
  });
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => { if (!authLoading && !user) navigate({ to: "/login" }); }, [authLoading, user, navigate]);

  async function onAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const original = e.target.files?.[0];
    if (!original || !user) return;
    if (!original.type.startsWith("image/")) return toast.error("Please pick an image file.");
    setUploadingAvatar(true);
    try {
      // Shrink in the browser so stored avatars stay small and load fast.
      const file = await compressImage(original);
      if (file.size > 5 * 1024 * 1024) throw new Error("Image still too large after compression.");
      // Storage rules require the first folder to be the owner's user id.
      const ext = file.type === "image/png" ? "png" : file.type === "image/webp" ? "webp" : file.type === "image/gif" ? "gif" : "jpg";
      const contentType = ["image/png", "image/webp", "image/gif"].includes(file.type) ? file.type : "image/jpeg";
      const path = `${user.id}/avatars/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("book-covers").upload(path, file, { upsert: true, contentType });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("book-covers").getPublicUrl(path);
      const url = pub.publicUrl;
      const { error: saveErr } = await supabase.from("profiles").upsert({ id: user.id, avatar_url: url }, { onConflict: "id" });
      if (saveErr) throw saveErr;
      setForm((f) => ({ ...f, avatar_url: url }));
      toast.success(
        original.size > file.size
          ? `Profile picture updated (optimised ${formatBytes(original.size)} → ${formatBytes(file.size)})`
          : "Profile picture updated",
      );
    } catch (err) {
      console.error("Avatar upload error:", err);
      toast.error("Could not upload your photo. Please try again.");
    } finally {
      setUploadingAvatar(false);
    }
  }


  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("*").eq("id", user.id).maybeSingle().then(({ data }) => {
      if (!data) return;
      setForm({
        full_name: data.full_name ?? "",
        phone_number: data.phone_number ?? "",
        age_group: data.age_group ?? "",
        address: (data as any).address ?? "",
        bio: (data as any).bio ?? "",
        favorite_quote: (data as any).favorite_quote ?? "",
        interested_categories: (data as any).interested_categories ?? [],
        avatar_url: (data as any).avatar_url ?? "",
      });
      setStats((s) => ({ ...s, since: new Date(data.created_at).toLocaleDateString() }));
      setStanding({
        strikes: data.strikes ?? 0,
        last_strike_at: data.last_strike_at ?? null,
        strikes_reset_at: data.strikes_reset_at ?? null,
      });
      setTrust({
        verified: (data as any).verified ?? false,
        completed_swaps: (data as any).completed_swaps ?? 0,
        response_rate: (data as any).response_rate ?? 100,
        strikes: data.strikes ?? 0,
      });
    });
    supabase.from("user_stats").select("books_listed,exchanges_completed,trust_score").eq("id", user.id).maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        setStats((s) => ({ ...s, listed: data.books_listed ?? 0, exchanges: data.exchanges_completed ?? 0, trust: data.trust_score ?? 0 }));
      });
  }, [user]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    if (!form.phone_number.trim()) return toast.error("Mobile number is required.");
    if (!form.address.trim()) return toast.error("Location or short address is required.");
    setSaving(true);
    // Upsert so the profile row is created if missing (e.g. legacy accounts), then
    // re-select to reflect the persisted state in the UI immediately.
    const { error } = await supabase
      .from("profiles")
      .upsert({ id: user.id, ...form }, { onConflict: "id" });
    if (error) {
      setSaving(false);
      console.error("Profile save error:", error);
      return toast.error("Could not save your profile. Please try again.");
    }
    const { data: fresh, error: readErr } = await supabase
      .from("profiles").select("*").eq("id", user.id).maybeSingle();
    setSaving(false);
    if (readErr || !fresh) {
      console.error(readErr);
      return toast.error("Saved, but could not reload your profile. Please refresh.");
    }
    setForm({
      full_name: fresh.full_name ?? "",
      phone_number: fresh.phone_number ?? "",
      age_group: fresh.age_group ?? "",
      address: (fresh as any).address ?? "",
      bio: (fresh as any).bio ?? "",
      favorite_quote: (fresh as any).favorite_quote ?? "",
      interested_categories: (fresh as any).interested_categories ?? [],
      avatar_url: (fresh as any).avatar_url ?? "",
    });
    toast.success("Profile saved successfully");
  }


  if (authLoading || !user) return <div className="min-h-screen bg-background"><Navbar /></div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 py-10">
        <h1 className="font-serif text-3xl">Your profile</h1>

        <div className="mt-4 rounded-2xl bg-card p-5 shadow-[var(--shadow-card)]">
          <TrustBadges data={trust} />
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <Stat label="Completed swaps" value={trust.completed_swaps} highlight />
          <Stat label="Reply rate" value={`${trust.response_rate}%`} />
          <Stat label="Books listed" value={stats.listed} />
          <Stat label="Member since" value={stats.since || "—"} />
          <Stat label="Email" value={user.email ?? "—"} />
        </div>

        <AccountStanding {...standing} />

        <WishlistSection userId={user.id} editable />

        <form onSubmit={save} className="mt-8 space-y-4 rounded-2xl bg-card p-6 shadow-[var(--shadow-card)]">
          <div className="flex items-center gap-4">
            <div className="h-20 w-20 overflow-hidden rounded-full bg-primary-soft ring-2 ring-border">
              {form.avatar_url
                ? <img src={form.avatar_url} alt="Profile" className="h-full w-full object-cover" />
                : <div className="flex h-full w-full items-center justify-center text-2xl font-semibold text-primary">{form.full_name?.[0]?.toUpperCase() ?? user.email?.[0]?.toUpperCase() ?? "?"}</div>}
            </div>
            <div>
              <Label htmlFor="avatar" className="cursor-pointer text-sm font-medium text-primary hover:underline">
                {uploadingAvatar ? "Uploading…" : form.avatar_url ? "Change profile picture" : "Upload profile picture"}
              </Label>
              <Input id="avatar" type="file" accept="image/*" className="hidden" onChange={onAvatarChange} disabled={uploadingAvatar} />
              <p className="mt-1 text-xs text-muted-foreground">Any size — we automatically shrink it for you.</p>
            </div>
          </div>
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="phone">Mobile number *</Label>
            <Input id="phone" type="tel" required placeholder={PHONE_PLACEHOLDER} value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="address">Location or short address *</Label>
            <Input id="address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder={ADDRESS_PLACEHOLDER} list="saudi-cities" />
            <datalist id="saudi-cities">{SAUDI_CITIES.map((c) => <option key={c} value={`${c}, ${COUNTRY}`} />)}</datalist>
          </div>
          <div>
            <Label htmlFor="bio">Short bio</Label>
            <Textarea id="bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="What do you love to read?" maxLength={300} />
          </div>
          <div>
            <Label htmlFor="quote">Favorite quote</Label>
            <Input id="quote" value={form.favorite_quote} onChange={(e) => setForm({ ...form, favorite_quote: e.target.value })} placeholder="A line that moves you" maxLength={200} />
          </div>
          <div>
            <Label>Your age group</Label>
            <Select value={form.age_group} onValueChange={(v) => setForm({ ...form, age_group: v })}>
              <SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>{AGE_GROUPS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Interested in which type of books?</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {(CATEGORIES as readonly string[]).map((c) => {
                const checked = form.interested_categories.includes(c);
                return (
                  <label key={c} className="flex items-center gap-2 rounded-md border border-border p-2 text-sm">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setForm({
                        ...form,
                        interested_categories: checked
                          ? form.interested_categories.filter((x) => x !== c)
                          : [...form.interested_categories, c],
                      })}
                    />
                    <span>{c}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <Button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90">{saving ? "Saving…" : "Save changes"}</Button>
        </form>
      </div>
    </div>
  );
}

function AccountStanding({
  strikes, last_strike_at, strikes_reset_at,
}: { strikes: number; last_strike_at: string | null; strikes_reset_at: string | null }) {
  const monthsSinceLast = last_strike_at
    ? (Date.now() - new Date(last_strike_at).getTime()) / (1000 * 60 * 60 * 24 * 30.44)
    : 0;
  const monthsRemaining = Math.max(0, 6 - monthsSinceLast);
  const remainingLabel =
    monthsRemaining < 1
      ? "Resets in less than 1 month if no new reports"
      : `🕐 Resets automatically in ~${Math.ceil(monthsRemaining)} month${Math.ceil(monthsRemaining) === 1 ? "" : "s"} if no new reports`;

  return (
    <section className="mt-8 rounded-2xl bg-card p-6 shadow-[var(--shadow-card)]">
      <h2 className="text-lg font-semibold text-primary-dark">Account standing</h2>

      {strikes === 0 && (
        <div className="mt-4">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
            ✅ Good Standing
          </span>
          {strikes_reset_at && (
            <p className="mt-2 text-xs text-muted-foreground">
              Your strikes were reset on {new Date(strikes_reset_at).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {strikes >= 1 && strikes <= 2 && (
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-2" aria-label={`${strikes} of 3 strikes`}>
            {[1, 2, 3].map((i) => (
              <span
                key={i}
                className={`h-5 w-5 rounded-full border ${i <= strikes ? "border-destructive bg-destructive" : "border-muted-foreground/30 bg-transparent"}`}
              />
            ))}
            <span className="ml-2 text-sm text-muted-foreground">{strikes} / 3</span>
          </div>
          <p className="text-sm text-muted-foreground">{remainingLabel}</p>

          {strikes === 2 && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              🚨 Final Warning: You have 2 strikes. One more damage report closes your account permanently. You can still reset your count by staying report-free for 6 months.
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Note: failing to return a borrowed book results in an immediate permanent ban, regardless of strike count.
          </p>
        </div>
      )}

      {strikes >= 3 && (
        <div className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          🚫 Account permanently closed.
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string | number; highlight?: boolean }) {
  return (
    <div className={`rounded-2xl p-4 shadow-[var(--shadow-card)] ${highlight ? "bg-gradient-to-br from-primary to-primary/80 text-white" : "bg-card"}`}>
      <p className={`text-xs uppercase tracking-wide ${highlight ? "text-white/80" : "text-muted-foreground"}`}>{label}</p>
      <p className={`mt-1 truncate font-semibold ${highlight ? "text-white" : "text-primary-dark"}`}>{value}</p>
    </div>
  );
}
